import { makeStyles } from '@mui/styles';
import CircularProgress from 'apollo-react/components/CircularProgress';
import Grid from 'apollo-react/components/Grid';
import Loader from 'apollo-react/components/Loader';
import Typography from 'apollo-react/components/Typography';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

import {
  appMigrationImport,
  cancelAppMigrationFile,
  clearAppMigrationImportItem,
  downloadLogFile,
  initiateImportProcess,
  setAppMigrationCurrentImportKey,
} from './actions/import.actions';
import ConfirmCancelDialog from './components/common/ConfirmCancelDialog';
import ImportFooter from './components/import/ImportFooter';
import { ImportInfo } from './components/import/ImportInfo';
import { StatusTable } from './components/import/StatusTable';
import { UploadImportFile } from './components/import/UploadImportFile';
import {
  cancelModalText,
  FORM_LOG_FILE_NAME,
  IMPORT_STEPS,
  NEW_IMPORT_KEY,
  STEPS_DESCRIPTION,
  STEPS_TO_SHOW_INFO,
} from './constants/import.constants';
import { getCurrentImportKey, getImportProp } from './selectors/import.selectors';
import { equalityFn } from './utils/import.utils';
import { StepProgress } from '../components/StepProgress';
import { APP_MIGRATION_PAGE } from '../constants/Urls';
import { useTranslation } from '../custom-hooks/useTranslation';

const useStyles = makeStyles({
  container: {
    display: 'flex',
  },
  main: {
    padding: '20px 40px',
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 'calc(100vh - 164px)',
  },
  footer: {},
  title: {
    fontSize: '24px',
    fontWeight: 600,
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 1,
    letterSpacing: 'normal',
    color: '#000',
    marginLeft: 17,
    padding: '24px 20px 18px 5px',
  },
  loader: {
    position: 'relative',
    textAlign: 'center',
    height: 180,
  },
});

const AppMigrationImport = () => {
  const translateString = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();
  const history = useHistory();

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isConfirmCancelDialogOpened, setIsConfirmCancelDialogOpened] = useState(false);

  const importKey = useSelector(getCurrentImportKey, equalityFn);

  const importStep = useSelector(getImportProp(importKey, 'importStep'));
  const importStatuses = useSelector(getImportProp(importKey, 'importStatuses'));
  const isLoading = useSelector(getImportProp(importKey, 'isLoading'));
  const hasImportError = useSelector(getImportProp(importKey, 'hasImportError'));
  const zipFieldId = useSelector(getImportProp(importKey, 'zipFieldId'));

  const totalItems = useSelector(getImportProp(importKey, 'totalItems'));
  const processedItems = useSelector(getImportProp(importKey, 'processedItems'));

  const progress = useMemo(() => Math.round((processedItems * 100) / totalItems), [
    processedItems,
    totalItems,
  ]);

  const downloadImportResult = () => {
    dispatch(downloadLogFile({ importKey: zipFieldId, fileName: FORM_LOG_FILE_NAME }));
  };

  const handleImportClick = () => {
    dispatch(appMigrationImport({ importKey }));
  };

  const handleCancel = () => {
    if (importStep === IMPORT_STEPS.FILE_PROCESSING) {
      setIsConfirmCancelDialogOpened(true);
    } else {
      setSelectedFiles([]);
      dispatch(clearAppMigrationImportItem(importKey));
      history.push(APP_MIGRATION_PAGE);
    }
  };

  const handleConfirmCancel = () => {
    setIsConfirmCancelDialogOpened(false);
    dispatch(
      cancelAppMigrationFile({
        importKey,
        appMigrationZipFileId: zipFieldId,
        isCancelExportProcess: false,
      })
    );
  };

  const startNewImport = () => {
    setSelectedFiles([]);
    dispatch(clearAppMigrationImportItem(importKey));
  };

  useEffect(() => {
    document.title = translateString('Import Application Components');

    if (!importKey) {
      dispatch(setAppMigrationCurrentImportKey(NEW_IMPORT_KEY));
      dispatch(initiateImportProcess(NEW_IMPORT_KEY));
    }

    return () => {
      dispatch(clearAppMigrationImportItem(importKey));
      setSelectedFiles([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importKey]);

  const isUploadFileStep = importStep === IMPORT_STEPS.UPLOAD_FILE;
  const isInitialStep = importStep === IMPORT_STEPS.INITIAL_STEP;
  const isFileProcessingStep = importStep === IMPORT_STEPS.FILE_PROCESSING;

  const showImportInfo =
    hasImportError || STEPS_TO_SHOW_INFO.includes(importStep) || (isUploadFileStep && !isLoading);

  const showFileUpload = !isLoading && isInitialStep;
  const showItemsTable =
    (importStep > IMPORT_STEPS.INITIAL_STEP && !isLoading) || isFileProcessingStep;

  return (
    <Grid container className={classes.container}>
      <Grid item xs={12}>
        <Typography className={classes.title}>
          {translateString('Import Application Components')}
        </Typography>
      </Grid>
      <Grid item xs={12} className={classes.main}>
        <StepProgress activeStep={importStep} steps={STEPS_DESCRIPTION} hasError={hasImportError} />
        {isLoading && (
          <div className={classes.loader}>
            {isFileProcessingStep ? (
              <CircularProgress
                value={progress}
                thickness={3}
                statusText={translateString(`Processing ${processedItems} out of ${totalItems}`)}
              />
            ) : (
              <Loader isInner />
            )}
          </div>
        )}
        {showImportInfo && (
          <ImportInfo
            importStatuses={importStatuses}
            importKey={importKey}
            importStep={importStep}
          />
        )}
        {showFileUpload && (
          <UploadImportFile
            importKey={importKey}
            selectedFiles={selectedFiles}
            setSelectedFiles={setSelectedFiles}
          />
        )}
        {showItemsTable && (
          <StatusTable
            rows={importStatuses}
            importKey={importKey}
            zipFieldId={zipFieldId}
            importStep={importStep}
            isLoading={isLoading}
          />
        )}
        {isConfirmCancelDialogOpened && (
          <ConfirmCancelDialog
            handleConfirmCancel={handleConfirmCancel}
            onClose={() => setIsConfirmCancelDialogOpened(false)}
            open={isConfirmCancelDialogOpened}
            text={cancelModalText}
          />
        )}
      </Grid>
      <Grid item xs={12} className={classes.footer}>
        <ImportFooter
          isLoading={isLoading}
          hasImportError={hasImportError}
          importStep={importStep}
          importStatuses={importStatuses}
          startNewImport={startNewImport}
          downloadImportResult={downloadImportResult}
          handleImportClick={handleImportClick}
          handleCancel={handleCancel}
        />
      </Grid>
    </Grid>
  );
};

export default AppMigrationImport;
